<template>
  <div :class="$style.container">
    <div :class="$style.info">
      <h3 :class="$style.infoHeader">
        {{ storefront.contactInfoTitle }}
      </h3>
      <div :class="$style.infoContent">
        <div>
          <img
            v-if="distributor.distImage"
            :class="$style.image"
            :src="distributor.distImage"
            alt="Profile Image"
          />
          <NsLogo v-else lock="simple" solid :colors="['FFFFFF']" storefront />
        </div>
        <div :class="$style.infoContentDetails">
          <h3 v-if="distributor.distName" :class="$style.name">
            {{ distributor.distName }}
          </h3>
          <div v-if="distributor.distEmail" :class="$style.email">
            {{ distributor.distEmail }}
          </div>
          <div v-if="distributor.distPhone" :class="$style.phone">
            {{ distributor.distPhone }}
          </div>
          <div
            v-if="(distributor.distSocialMedia || []).length > 0"
            :class="$style.socialLinks"
          >
            <a
              v-for="link in distributor.distSocialMedia"
              :key="link.label"
              :class="$style.socialLink"
              :href="link.href"
              target="_blank"
            >
              <NsIcon :icon-name="link.iconName" color="#fff" />
              <span>{{ link.label }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <div :class="$style.logo">
      <span :class="$style.logoLabel">
        {{ storefront.poweredByLabel }}
      </span>
      <NsLogo lock="lockup" solid :colors="['FFFFFF']" />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  events,
  FirebaseUtil,
  nuskin,
  RunConfigService,
  StoreFrontSponsorStorageService,
  PersonalOfferStorageService
} from "@nuskin/ns-util";
import { NsIcon } from "@nuskin/ns-icon";

import NsLogo from "./NsLogo.vue";

export default {
  name: "NsFooterStorefront",
  components: { NsIcon, NsLogo },
  props: {
    storefront: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    distributor: {}
  }),
  mounted() {
    this.handleStorefront();
    events.subscribe(events.shop.PERSONAL_OFFER_LOADED, this.handleStorefront);
  },
  destroyed() {
    events.unsubscribe(
      events.shop.PERSONAL_OFFER_LOADED,
      this.handleStorefront
    );
  },
  methods: {
    getFirebaseConfig() {
      let config;
      if (
        nuskin.configuration.siteUrl.indexOf("www") >= 0 &&
        nuskin.configuration.siteUrl.indexOf("www.dev.") === -1 &&
        nuskin.configuration.siteUrl.indexOf("www.tst.") === -1
      ) {
        FirebaseUtil.init("prod");
      } else {
        FirebaseUtil.init("test");
      }
      config = FirebaseUtil.getConfig();
      return config;
    },
    async getStoreOwnerId(subdomain) {
      const url =
        this.getFirebaseConfig().databaseURL +
        "/storefront/subdomains/" +
        subdomain +
        ".json";
      return axios.get(url);
    },
    async getStorefrontOwnerData(userId) {
      const url =
        this.getFirebaseConfig().databaseURL +
        "/storefront/users/" +
        userId +
        ".json";
      return axios.get(url);
    },
    async handleStorefront() {
      if (PersonalOfferStorageService.isPersonalOffer()) {
        this.setStorefrontDataFromPersonalOffer();
        return;
      }

      // If not personal-offer, get userId and data via storage or axios
      const host = window.location.hostname;
      const baseUrl = RunConfigService.getBaseUrl();
      const subdomain = host.includes(".")
        ? host.split(".")[0]
        : baseUrl.split(".")[0].replace("https://", "");
      const { data: userId } = (await this.getStoreOwnerId(subdomain)) || {};

      if (!userId) {
        console.error("Couldn't find distributor id.");
        return;
      }

      const { data: sponsorFromStorage } =
        StoreFrontSponsorStorageService.getStoreFrontSponsor() || {};

      // use session storage if the correct sponsor is saved there, before using service
      if (sponsorFromStorage && sponsorFromStorage.sapId === userId) {
        this.setStorefrontData(sponsorFromStorage);
      } else {
        const { data: sponsor } =
          (await this.getStorefrontOwnerData(userId)) || {};
        this.setStorefrontData(sponsor);
      }
    },
    async setStorefrontDataFromPersonalOffer() {
      // get previous pageName if referred
      if (document.referrer) {
        const prevPage = document.referrer.split("/");
        const pageName = prevPage[prevPage.length - 1];
        const prevPageName = pageName.substring(0, pageName.indexOf("."));

        // use storefront storage if "checkout" or "product"
        if (prevPageName === "checkout" || prevPageName === "product") {
          const {
            data: sponsorFromStorage
          } = StoreFrontSponsorStorageService.getStoreFrontSponsor();
          if (sponsorFromStorage) {
            this.setStorefrontData(sponsorFromStorage);
            return;
          }
        }
      }

      // if not "checkout" or "product", use userId from params to get data
      let userId = new URLSearchParams(window.location.search).get("userId");
      if (!userId) {
        const poData = PersonalOfferStorageService.getPersonalOffer();
        if (poData) {
          userId = poData.sapId;
        }
      }
      const { data: sponsor } =
        (await this.getStorefrontOwnerData(userId)) || {};
      this.setStorefrontData(sponsor);
    },
    setStorefrontData(sponsor) {
      if (!sponsor) {
        console.error("Couldn't find distributor data.");
        return;
      }

      const baseUrl = RunConfigService.getBaseUrl();
      const isPersonalOffer = PersonalOfferStorageService.isPersonalOffer();
      const distImageUrl = isPersonalOffer
        ? sponsor.photoURL || sponsor.profilePhoto
        : sponsor.profilePhoto || sponsor.photoURL;
      this.distributor = {
        distImage: distImageUrl.startsWith("/")
          ? baseUrl + distImageUrl
          : distImageUrl,
        distName: sponsor.displayName,
        distEmail: sponsor.email,
        distPhone: sponsor.phone,
        distSocialMedia: (sponsor.socialAccounts || []).map(link => ({
          href: !link.url.includes("http") ? `https://${link.url}` : link.url,
          iconName: `icon-${link.id}`,
          label: link.name
        }))
      };
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/colors";

$z-dropdown: map.get(other.$otherUtils, z-dropdown);

.container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 15px;
  width: 100%;
  color: #ffffff;
  background-color: map.get(colors.$themeColors, color-interactive-9);
}

.info {
  width: 100%;
}

.infoHeader {
  margin-bottom: 20px;
  color: #ffffff;
  font-size: 20px;
  font-weight: 300;
  line-height: 24px;
  text-transform: uppercase;
}

.infoContent {
  display: flex;
}

.image {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  border: 1px solid #272525;
}

.infoContentDetails {
  margin-left: 15px;
}

.name {
  margin-bottom: 5px;
  color: #ffffff;
  font-size: 20px;
  font-weight: normal;
}

.email {
  margin-bottom: 5px;
}

.phone {
  margin-bottom: 5px;
}

.socialLinks {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -5px;
}

a.socialLink {
  display: flex;
  margin: 0 5px 5px;
  color: #ffffff;
  text-decoration: none;

  span {
    margin-left: 5px;
  }
}

.logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-top: 30px;
}

.logoLabel {
  margin-right: 10px;
  font-size: 0.8em;
  font-weight: 600;
  text-align: right;
  text-transform: uppercase;
}

@media (min-width: 768px) {
  .container {
    flex-direction: row;
    padding: 30px 20px;
  }

  .logo {
    align-self: flex-end;
    margin: 0;
  }
}

@media (min-width: 1024px) {
  .container {
    &:before {
      content: "";
      height: 100%;
      width: 100vw;
      position: absolute;
      left: calc((100vw - 100%) / -2);
      background: map.get(colors.$themeColors, color-interactive-9);
      z-index: -1;
    }
  }
}
</style>
